import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import {
  CssBaseline,
  ThemeProviderV4,
  StyledEngineProvider,
  StylesProviderV4,
  makeStyles,
  ThemeProvider,
  Alert,
  AlertTitle,
} from '~/lib/bn-material-ui';
import { useStoreValues, store, generateClassName, muiTheme } from '~/utility/BinSentry-ui-utility';
import BnSnackbarProvider from './common/bn-snackbar-provider';
import { BnDynamicComponent } from './common/bn-dynamic-component';
import { BnErrorBoundary } from '~/common/bn-error-boundary';

const SpaContext = React.createContext({});

export default function Root(props) {
  const { path } = useStoreValues(['path']);
  return (
    <BnErrorBoundary locationTag='root-react-component'>
      <SpaContext.Provider value={props}>
        <StyledEngineProvider generateClassName={generateClassName} injectFirst>
          <StylesProviderV4 generateClassName={generateClassName}>
            <ThemeProvider theme={muiTheme}>
              <ThemeProviderV4 theme={muiTheme}>
                <BnSnackbarProvider>
                  <ActiveView path={path} />
                </BnSnackbarProvider>
              </ThemeProviderV4>
            </ThemeProvider>
          </StylesProviderV4>
        </StyledEngineProvider>
      </SpaContext.Provider>
    </BnErrorBoundary>
  );
}

const useStyles = makeStyles(() => ({
  fallbackContainer: {
    width: 'calc(100% - 2rem)',
    margin: '1rem',
  },
}));

function ActiveView({ path }) {
  const styles = useStyles();
  return (
    <BnErrorBoundary
      key={path}
      locationTag='ui-customers-page'
      fallbackClassName={styles.fallbackContainer}
    >
      {getActiveView({ path })}
    </BnErrorBoundary>
  );
}

// Also see index.jsx - reactPaths
const SIMPLE_PATH_PREFIX_MATCHING = {
  '/confirm': async () => (await import('~/user/confirm-registration-page.jsx')).default,
  '/insights': async () => (await import('~/insights/insights-index.tsx')).default,
  '/customers': async () => {
    if (window.location.host === 'ui.prototype.dev.binsentry.com') {
      return (await import('~/feed-levels/feed-levels-page.jsx')).default;
    }
    return (await import('./organizations/customers/customers-view.jsx')).default;
  },
  '/feeds': async () => (await import('./feeds/feeds.jsx')).default,
  '/account': async () => (await import('./user/user-show.jsx')).default,
  '/animal-groups': async () => (await import('./animal-groups/animal-group-page.jsx')).default,
  '/barns': async () => (await import('~/organizations/barns/barn-page/barn-page.jsx')).default,
  '/bins': async () => (await import('~/bins/bins-index.jsx')).default,
  '/billing-management': async () => (await import('./billing/management/billing-management-index.jsx')).default,
  '/billing': async () => (await import('./billing/billing-index.jsx')).default,
  '/device-analysis': async () => (await import('~/device-analysis/device-analysis-index.jsx')).default,
  '/qbo-billing': async () => (await import('./billing/qbo-token-generation.jsx')).default,
  '/order-scheduler': async () => (await import('./orders/scheduler/order-scheduler.jsx')).default,
  '/events': async () => (await import('./issues/issues-index.tsx')).default,
  '/orders': async () => (await import('./orders/orders-index.jsx')).default,
  '/organizations': async () => (await import('~/organizations/organization-management/organizations-index.jsx')).default,
  '/feed-orders': async () => (await import('./orders/feed-orders/feed-order-page.jsx')).default,
  '/readings': async () => (await import('./readings/reading-viewer.jsx')).default,
  '/inventory': async () => (await import('~/inventory/inventory-page.jsx')).InventoryPage,
  '/work-order-locations': async () => (await import('./work-order-locations/work-order-locations-index.jsx')).default,
  '/internal': async () => (await import('~/internal/internal-tools-index.jsx')).default,
  '/notifications': async () => (await import('~/notifications/notifications-index.jsx')).default,
  '/data-labeling-review': async () => (await import('~/data-labeling-review/data-labeling-review.jsx')).default,
};

function getActiveView({ path }) {
  const simpleGetComponent = Object.entries(SIMPLE_PATH_PREFIX_MATCHING)
    .find(([prefix]) => path.startsWith(prefix))?.[1];
  if (simpleGetComponent) {
    return <BnDynamicComponent getComponent={simpleGetComponent} />;
  }
  if (path.startsWith('/feed-budgets/schedules')) {
    return <BnDynamicComponent
      getComponent={async () => (await import('./consumption-plans/consumption-plan-list.jsx')).default }
      componentProperties={{ type: 'schedule' }}
    />;
  }
  if (path.startsWith('/feed-budgets/templates')) {
    return <BnDynamicComponent
      getComponent={async () => (await import('./consumption-plans/consumption-plan-list.jsx')).default }
      componentProperties={{ type: 'template' }}
    />;
  }
  if (path.startsWith('/feed-budgets/show')) {
    return <BnDynamicComponent
      getComponent={async () => (await import('./consumption-plans/consumption-plan-show.jsx')).default }
    />;
  }
  if (path.startsWith('/feed-budgets/calendar')) {
    return <BnDynamicComponent
      getComponent={async () => (await import('./consumption-plan-editor/calendar-view/consumption-plan-calendar.jsx')).default }
    />;
  }
  if (path.startsWith('/print')) {
    const subpath = path.replace('/print', '');
    let child;
    if (subpath.startsWith('/feed-orders')) {
      child = <BnDynamicComponent
        getComponent={async () => (await import('./orders/feed-orders/feed-order-page.jsx')).default }
      />;
    } else if (subpath.startsWith('/group-reports')) {
      child = <BnDynamicComponent
        getComponent={async () => (await import('./flock-reports/flock-report-print-view.jsx')).default }
      />;
    } else {
      child = null;
    }

    return <PrintComponent>{child}</PrintComponent>;
  }

  return (
    <Alert severity='error'>
      <AlertTitle>Page Not Found</AlertTitle>
      The page you were looking for does not exist.
    </Alert>
  );
}

function PrintComponent({ children }) {
  const [{ apiToken }] = useCookies(['apiToken']);
  useEffect(() => {
    // When puppeteer is the client, set the token.  "print" is in the list of publicPaths, so user authentication is not required.
    if (apiToken) {
      store.dispatch({
        type: 'UPDATE_AUTHENTICATION',
        token: apiToken,
        subject: null,
      });
    }
  }, [apiToken]);

  // normally the CSS reset comes from ui-navigation.  When in print mode, we have no nav, so do the reset here.
  return <>
    <CssBaseline />
    {children}
  </>;
}

export { SpaContext };
